<template>
  <div v-if="wishlistFeatureEnabled" class="wishlist-button">
    <a href="#" class="wishlist-button__link" @click.stop="saveFavorite(item)">
      <PhHeartStraight
        :size="20"
        :color="wishlistHeartColor"
        :weight="activeWishlists.length > 0 ? 'fill' : 'regular'"
      />
      <span
        v-if="activeWishlists.length > 1"
        class="wishlist-button__count"
        :title="`In ${activeWishlists.length} wishlists`"
      >
        {{ activeWishlists.length }}
      </span>
    </a>

    <!-- Wishlist Selection Dialog -->
    <WishlistSelectionDialog
      :open="showWishlistDialog"
      :item="item"
      @close="showWishlistDialog = false"
      @added="onItemAddedToWishlist"
      @removed="onItemRemovedFromWishlist"
    />

    <!-- Wishlist Create Dialog -->
    <WishlistCreateDialog
      :open="showCreateWishlistDialog"
      @close="showCreateWishlistDialog = false"
      @created="onWishlistCreated"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useWishlists } from '@/composables/useWishlists.js'
import { PhHeartStraight } from '@phosphor-icons/vue'
import WishlistSelectionDialog from './WishlistSelectionDialog.vue'
import WishlistCreateDialog from './WishlistCreateDialog.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['wishlist-updated'])

const {
  wishlistsList,
  wishlistsGetBySkuCode,
  wishlistGetPrimaryWishlist,
  wishlistAddItem,
  wishlistRemoveItem,
  wishlistIsEnabled,
  wishlistsGetEditable,
  activeFilterIds
} = useWishlists()

const showWishlistDialog = ref(false)
const showCreateWishlistDialog = ref(false)

const wishlistFeatureEnabled = computed(() => wishlistIsEnabled())

const activeWishlists = computed(() => {
  const allWishlists = wishlistsGetBySkuCode(props.item.sku) || []

  // If no filter is active, return all wishlists containing this item
  if (activeFilterIds.value.length === 0) {
    return allWishlists
  }

  // If filter is active, only return wishlists that are in the filter
  return allWishlists.filter((wishlist) => activeFilterIds.value.includes(wishlist.id))
})

// Determines the primary wishlist for display and removal logic
// Priority: default wishlist > latest updated wishlist > first wishlist
const primaryWishlist = computed(() => wishlistGetPrimaryWishlist(activeWishlists.value))

const wishlistHeartColor = computed(() => {
  return primaryWishlist.value?.color?.slcolor || 'var(--wa-color-neutral-50)'
})

async function saveFavorite(item) {
  const currentWishlists = wishlistsGetBySkuCode(item.sku)
  const allWishlists = wishlistsList()

  // If no wishlists exist, show create dialog
  if (allWishlists.length === 0) {
    showCreateWishlistDialog.value = true
    return
  }

  // Get editable wishlists for adding items
  const editableWishlists = wishlistsGetEditable()

  // If only one wishlist exists and item is not in it, add to it
  if (allWishlists.length === 1 && currentWishlists.length === 0) {
    await wishlistAddItem(allWishlists[0].id, item.sku)
    onItemAddedToWishlist()
    return
  }

  // If only one wishlist exists and item is in it, remove from it
  if (allWishlists.length === 1 && currentWishlists.length === 1) {
    await wishlistRemoveItem(allWishlists[0].id, item.sku)
    onItemRemovedFromWishlist()
    return
  }

  // Special case: If multiple wishlists exist but only one is editable (not read-only)
  // and the item is not in any wishlist, automatically add to the editable one
  if (editableWishlists.length === 1 && currentWishlists.length === 0) {
    await wishlistAddItem(editableWishlists[0].id, item.sku)
    onItemAddedToWishlist()
    return
  }

  // If multiple wishlists exist, show selection dialog for multi-select
  showWishlistDialog.value = true
}

function onItemAddedToWishlist() {
  emit('wishlist-updated')
}

function onItemRemovedFromWishlist() {
  emit('wishlist-updated')
}

async function onWishlistCreated(newWishlist) {
  // After creating a new wishlist, add the current item to it
  await wishlistAddItem(newWishlist.id, props.item.sku)
  emit('wishlist-updated')
}
</script>

<style scoped>
.wishlist-button {
  &__link {
    padding: var(--wa-space-s);
    margin-top: -8px;
    position: relative;
    text-decoration: none;
    display: block;
  }

  &__count {
    position: absolute;
    top: 2px;
    right: 2px;
    background: var(--wa-color-brand-40);
    color: white;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    font-size: 10px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    pointer-events: none;
  }
}
</style>
